"use client";

import React, { useEffect, useMemo, useState } from "react";
import type { Session } from "next-auth";
import { SessionProvider } from "next-auth/react";
import CartProvider from "./default/contexts/CartContext";
import SettingsProvider from "./default/contexts/SettingContext";
import { ProgressBar } from "./default/progress";
import { RTL } from "./default/rtl";
import ThemeProvider from "./default/theme/theme-provider";
import GlobalDefaultContextRegistry from "./default/global-context";
import { ToastContainer } from "react-toastify";
import HeadClient from "./default/head/head-client";
import { getGlobalContextValue } from "./utils";
import { JSONHotReloadProvider } from "../../utils/json-hot-reload-client";
import { fontInter } from "./default/theme/font-family/fonts/inter";
import { fontOpenSans } from "./default/theme/font-family/fonts/open-sans";
import { fontPlusJakartaSans } from "./default/theme/font-family/fonts/plus-jakarta-sans";
import { fontReadexPro } from "./default/theme/font-family/fonts/readex-pro";
import { LoadDeferredSwiperStyles } from "./load-deferred-swiper-styles";
import { LoadDeferredToastifyStyles } from "./load-deferred-toastify-styles";
import { LoadDeferredSimplebarStyles } from "./load-deferred-simplebar-styles";
import { resolveSiteBuilderTheme } from "src/lib/theme/salify-ui-theme";

export const openSans = fontOpenSans;

export interface VariantProps {
  theme?:
    | "GIFT"
    | "HEALTH"
    | "DEFAULT"
    | "GROCERY"
    | "FURNITURE"
    | "FOOTMART"
    | "BAZZAR"
    | "NOVAPRESS"
    | "HAIANH"
    | "LUXE"
    | "DATHACONS";
}

/** Map host to body font (layout-driven theme; see `layout.theme` / `theme-provider`). */
const domainFontMap: Record<string, "openSans" | "inter" | "plusJakarta" | "readexPro"> = {
  "localhost:3101": "inter",
  "maylanhthanhan.vn": "readexPro",
  "haianh.salify.vn": "inter",
  "dathacons.local": "inter",
  "dathacons.com.vn": "inter",
};

const getFont = (host: string) => {
  if (domainFontMap[host]) {
    const t = domainFontMap[host];
    if (t === "inter") return fontInter;
    if (t === "plusJakarta") return fontPlusJakartaSans;
    if (t === "readexPro") return fontReadexPro;
    return openSans;
  }

  for (const [domain, fontType] of Object.entries(domainFontMap)) {
    if (host.endsWith(domain)) {
      if (fontType === "inter") return fontInter;
      if (fontType === "plusJakarta") return fontPlusJakartaSans;
      if (fontType === "readexPro") return fontReadexPro;
      return openSans;
    }
  }

  return openSans;
};

const loadingStateCss = `
@keyframes salifyLoadingGradientBG {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}
.salify-loading-root .salify-loading-container {
  padding: 2rem;
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}
.salify-loading-root .salify-loading-icon-wrap {
  position: relative;
  width: 120px;
  height: 120px;
}
.salify-loading-root .salify-loading-animated-icon {
  transform-origin: center;
  animation: salifyLoadingRotate 2s linear infinite;
}
.salify-loading-root .salify-loading-circle {
  stroke-dasharray: 283;
  stroke-dashoffset: 283;
  animation: salifyLoadingDrawCircle 2s ease-in-out infinite;
}
.salify-loading-root .salify-loading-check {
  stroke-dasharray: 100;
  stroke-dashoffset: 100;
  animation: salifyLoadingDrawCheck 2s ease-in-out infinite;
}
.salify-loading-root .salify-loading-dash {
  stroke-dasharray: 50;
  stroke-dashoffset: 50;
  animation: salifyLoadingDrawDash 2s ease-in-out infinite;
}
@keyframes salifyLoadingRotate {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
@keyframes salifyLoadingDrawCircle {
  0% { stroke-dashoffset: 283; }
  50% { stroke-dashoffset: 0; }
  100% { stroke-dashoffset: 283; }
}
@keyframes salifyLoadingDrawCheck {
  0% { stroke-dashoffset: 100; }
  50% { stroke-dashoffset: 0; }
  100% { stroke-dashoffset: 100; }
}
@keyframes salifyLoadingDrawDash {
  0% { stroke-dashoffset: 50; }
  50% { stroke-dashoffset: 0; }
  100% { stroke-dashoffset: 50; }
}
`;

function LoadingState({ font }: { font: any }) {
  return (
    <div
      className="salify-loading-root"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        background: "linear-gradient(45deg, #FF6B6B, #4ECDC4, #45B7D1, #96C93D)",
        backgroundSize: "300% 300%",
        animation: "salifyLoadingGradientBG 15s ease infinite",
        fontFamily: font.className,
      }}
    >
      <style dangerouslySetInnerHTML={{ __html: loadingStateCss }} />
      <div className="salify-loading-container">
        <div className="salify-loading-icon-wrap">
          <svg
            className="salify-loading-animated-icon"
            viewBox="0 0 100 100"
            width="120"
            height="120"
          >
            <circle
              className="salify-loading-circle"
              cx="50"
              cy="50"
              r="45"
              fill="none"
              stroke="white"
              strokeWidth="4"
            />
            <path
              className="salify-loading-check"
              d="M30 50 L45 65 L70 35"
              fill="none"
              stroke="white"
              strokeWidth="4"
            />
            <path
              className="salify-loading-dash"
              d="M25 50 L75 50"
              fill="none"
              stroke="white"
              strokeWidth="4"
            />
          </svg>
        </div>
      </div>
    </div>
  );
}

function ClientLayout({
  children,
  global,
  font,
  session,
}: {
  children: React.ReactNode;
  global: any;
  font: any;
  session: Session | null;
}) {
  const siteBuilderTheme = useMemo(() => resolveSiteBuilderTheme(global), [global]);
  const [shellReady, setShellReady] = useState(false);

  useEffect(() => {
    setShellReady(true);
  }, []);

  return (
    <JSONHotReloadProvider
      enabled={process.env.NODE_ENV === "development"}
      showStatus={true}
      statusPosition="bottom-right"
    >
      <LoadDeferredSwiperStyles />
      <LoadDeferredSimplebarStyles />
      <LoadDeferredToastifyStyles />
      <GlobalDefaultContextRegistry {...global}>
        <SessionProvider session={session ?? undefined} refetchOnWindowFocus={false}>
          <CartProvider>
            <SettingsProvider>
              <ThemeProvider
                theme={siteBuilderTheme.id}
                themeOptionsOverride={siteBuilderTheme.muiThemeOverride}
              >
                {shellReady ? <ProgressBar /> : null}
                <RTL>{children}</RTL>
              </ThemeProvider>
            </SettingsProvider>
          </CartProvider>
        </SessionProvider>
        {shellReady ? <ToastContainer /> : null}
      </GlobalDefaultContextRegistry>
    </JSONHotReloadProvider>
  );
}

function SalifyRootLayout(
  props: VariantProps &
    React.PropsWithChildren<{
      initialGlobal?: unknown;
      initialHost?: string;
      session?: Session | null;
    }>,
) {
  const [global, setGlobal] = useState<any>(() =>
    props.initialGlobal !== undefined ? props.initialGlobal : null,
  );
  const [font, setFont] = useState(() => getFont(props.initialHost || ""));

  useEffect(() => {
    const ig = props.initialGlobal as Record<string, unknown> | null | undefined;
    if (ig != null && typeof ig === "object" && Object.keys(ig).length > 0) {
      return;
    }
    getGlobalContextValue().then(setGlobal);
  }, [props.initialGlobal]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setFont(getFont(window.location.host));
    }
  }, []);

  if (!global) {
    return (
      <html lang="en" suppressHydrationWarning>
        <body className={font.className}>
          <LoadingState font={font} />
        </body>
      </html>
    );
  }

  return (
    <html lang="en" suppressHydrationWarning>
      <body className={font.className}>
        <HeadClient gtag={global?.metadata?.analytics?.gtag || ""} />
        {global?.metadata?.analytics?.gtag && (
          <noscript>
            <iframe
              title="Google Tag Manager"
              src={`https://www.googletagmanager.com/ns.html?id=${global.metadata.analytics.gtag}`}
              height="0"
              width="0"
              style={{ display: "none", visibility: "hidden" }}
            ></iframe>
          </noscript>
        )}
        <ClientLayout global={global} font={font} session={props.session ?? null}>
          {props.children}
        </ClientLayout>
      </body>
    </html>
  );
}

export default function RootLayout({
  children,
  initialGlobal,
  initialHost,
  session,
}: {
  children: React.ReactNode;
  initialGlobal?: unknown;
  initialHost?: string;
  session?: Session | null;
}) {
  return (
    <SalifyRootLayout initialGlobal={initialGlobal} initialHost={initialHost} session={session}>
      {children}
    </SalifyRootLayout>
  );
}
